// apps/web/src/lib/useCountUp.ts
import { useEffect, useRef, useState } from "react";

export function prefersReducedMotion(): boolean {
  return typeof window !== "undefined"
    && typeof window.matchMedia === "function"
    && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** easeOutCubic on a normalized progress t∈[0,1]. */
export function easeOutCubic(t: number): number {
  const c = Math.max(0, Math.min(1, t));
  return 1 - Math.pow(1 - c, 3);
}

/** Interpolated count value at progress t, rounded — the value the hook renders. */
export function countAt(start: number, target: number, t: number): number {
  return Math.round(start + (target - start) * easeOutCubic(t));
}

/**
 * Animate a number from its previous value to `target` over `durationMs`,
 * easing out. Returns `target` immediately when the user prefers reduced motion
 * (or when rAF is unavailable). Pure timing logic; rendering is the caller's.
 */
export function useCountUp(target: number, durationMs = 600): number {
  const [value, setValue] = useState(target);
  const fromRef = useRef(target);

  useEffect(() => {
    const start = fromRef.current;
    if (
      start === target ||
      prefersReducedMotion() ||
      typeof requestAnimationFrame !== "function"
    ) {
      fromRef.current = target;
      setValue(target);
      return;
    }

    let raf = 0;
    let startedAt = 0;
    const tick = (ts: number) => {
      if (!startedAt) startedAt = ts;
      const t = Math.min(1, (ts - startedAt) / durationMs);
      setValue(countAt(start, target, t));
      if (t < 1) raf = requestAnimationFrame(tick);
      else fromRef.current = target;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);

  return value;
}
